import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

const issuer = import.meta.env.VITE_OIDC_ISSUER as string | undefined;
const clientId = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined) ?? 'outss-ledger-web';
/** The dev IdP's public demo sign-in is on (ADR-0020); Keycloak and Cognito have none. */
const demoLogin = import.meta.env.VITE_DEMO_LOGIN === '1';

/** Who the public demo signs in as (ADR-0020): the practice's accountant or a client's owner. */
export type DemoPersona = 'accountant' | 'owner';
export const isDemoPersona = (v: unknown): v is DemoPersona => v === 'accountant' || v === 'owner';
/**
 * Where the identity provider lets a user change their password and two-step verification
 * (Keycloak: `{issuer}/account`). Unset for the dev IdP, which has a shared password.
 */
const accountUrl = (import.meta.env.VITE_OIDC_ACCOUNT_URL as string | undefined) || undefined;

/** OIDC authorization-code flow with PKCE against Keycloak (local) or Cognito (AWS). */
export const auth = {
  enabled: Boolean(issuer),
  /** Try the demo: one click signs in as a demo persona, without a password. */
  demo: Boolean(issuer) && demoLogin,
  /** The IdP's account page (password, two-step verification), when it has one. */
  accountUrl: issuer ? accountUrl : undefined,
  manager: issuer
    ? new UserManager({
        authority: issuer,
        client_id: clientId,
        redirect_uri: `${window.location.origin}/auth/callback`,
        post_logout_redirect_uri: window.location.origin,
        response_type: 'code',
        scope: 'openid profile email',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        automaticSilentRenew: true,
      })
    : undefined,

  async getUser(): Promise<User | null> {
    if (!auth.manager) return null;
    const user = await auth.manager.getUser();
    return user && !user.expired ? user : null;
  },

  async getAccessToken(): Promise<string | undefined> {
    return (await auth.getUser())?.access_token;
  },

  /**
   * Hand over to the identity provider. `email` pre-fills its form (login_hint); `signUp` asks it
   * for its registration page instead (OIDC prompt=create, which Keycloak and Cognito support).
   */
  signIn(
    returnTo: string = window.location.pathname,
    opts: { email?: string; signUp?: boolean } = {},
  ): Promise<void> {
    if (!auth.manager) return Promise.resolve();
    // Ending up back on the callback or a public page after sign-in would loop or strand the
    // user; open the dashboard instead.
    const state =
      returnTo.startsWith('/auth/') || ['/', '/login', '/signup', '/demo'].includes(returnTo)
        ? '/dashboard'
        : returnTo;
    return auth.manager.signinRedirect({
      state,
      ...(opts.email ? { login_hint: opts.email } : {}),
      ...(opts.signUp ? { prompt: 'create' } : {}),
    });
  },

  /**
   * The public demo: the dev IdP signs in the chosen persona without a form (extra parameter
   * demo=accountant or demo=owner, still code + PKCE) and the app opens on the dashboard.
   */
  signInDemo(persona: DemoPersona): Promise<void> {
    if (!auth.manager) return Promise.resolve();
    return auth.manager.signinRedirect({
      state: '/dashboard',
      extraQueryParams: { demo: persona },
    });
  },

  /**
   * The persona of the stored sign-in, expired or not, when it was the demo's: its session is
   * signed in again as the same persona, not sent to the password form (the dev IdP forgets
   * refresh tokens when it restarts). Null when it was not the demo.
   */
  async wasDemo(): Promise<DemoPersona | null> {
    if (!auth.demo) return null;
    const roles = (await auth.manager?.getUser())?.profile['roles'];
    if (!Array.isArray(roles) || !roles.includes('demo')) return null;
    return roles.includes('client_owner') ? 'owner' : 'accountant';
  },

  signOut(): Promise<void> {
    return auth.manager ? auth.manager.signoutRedirect() : Promise.resolve();
  },
};
